import {
  ANALYTICS_EVENTS,
  MARKET,
  MENU_WELCOME_DISMISSED_KEY,
  ORDER_TYPES,
  SAVED_DIETARY_PREFERENCES_KEY,
} from '@nandosaus/constants';
import { get, isEmpty, map, isNil, isObject, toString, filter } from 'lodash';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import moment from 'moment';
import PropTypes from 'prop-types';

import { analytics } from '@analytics';
import { Col, Container, Row, Flex } from '@components/grid';
import { H4 } from '@components/typography/new';
import { PointsRedemptionBanner } from '@components/points-redemption/points-redemption-banner';
import { CartPreviewModal } from '@components/cart-preview-modal';
import { DefaultState } from '@components/default';
import { ErrorBoundary } from '@components/error-boundary';
import { fetchCMSContent } from '@utils/fetch-cms-content';
import { floodlightPageLoadEventPropTypes } from '@prop-types';
import { HolidaySurchargeAlert } from '@components/holiday-surcharge-alert';
import { HalalAlert } from '@components/halal-alert';
import { InPageNav, NAV_ITEMS_COUNT, LG_PLUS_BREAKPOINT, XL_PLUS_BREAKPOINT } from '@components/in-page-nav';
import { Layout } from '@components/layout';
import { localStorage } from '@utils/local-storage';
import { MenuDisclaimer } from '@components/menu-disclaimer';
import { MenuItemCard } from '@components/card';
import { OrderSettingsBanner } from '@components/order-settings-banner';
import { OrderSettingsModalCatering } from '@components/order-settings-modal-catering';
import { ShortLeadTimeErrorModal } from '@components/order-settings-modal-catering/short-lead-time-error-modal';
import { ProductCategoryList } from '@components/product-category-list';
import { ProductDetailModal } from '@components/product-detail-modal';
import { seoPropDefaults, seoPropTypes } from '@utils/extract-seo-meta-from-entry';
import { SkeletonLoader } from '@components/skeleton-loader';
import { StockShortageAlert } from '@components/stock-shortage-alert';
import { useCateringDateValidation } from '@hooks/use-catering-date-validation';
import { useCateringShortLeadModal } from '@hooks/use-catering-short-lead-modal';
import { useCookie } from '@hooks/use-cookie';
import { useFeature } from '@hooks/use-feature';
import { useIsClient } from '@hooks/use-is-client';
import { useMarket } from '@hooks/use-market';
import { withAppProps } from '@utils/with-app-props';
import { CartClearModal } from '@components/cart-clear-modal';
import { RecommendationEngineModal } from '@components/recommendation-engine-modal';
import { useCartPersistence } from '@hooks/use-cart-persistence';
import { useCateringPreSet } from '@hooks/use-catering-pre-set';
import { PromotionList } from '@components/offer/promotion-list';
import { TabOrderShareModal } from '@components/tab-order-share-modal';
import { useMediaQuery } from 'react-responsive';
import { useShouldEnableRecommendationEngine } from '@hooks/use-should-enable-recommendation-engine';
import { useCartVisibility } from '@hooks/use-cart-visibility';
import { useUpsellModalHandler } from '@hooks/use-upsell-modal-handler';
import { useBrazeUpsellContent } from '@hooks/use-braze-upsell-content';
import { useTheme } from '@hooks/useTheme';
import { UpsellModal } from '@components/upsell-modal';

import { SearchBar } from '@components/search-bar';
import { GlobalBanner as SurpriseAndDelightGlobalBanner } from '@components/surprise-and-delight/global-banner';
import { useSearch } from '@hooks/use-search';
import { MenuPastOrders } from '@components/menu-past-orders';
import MenuActionCard from '../menu/menu-action-card';
import Error from '../_error';

const MenuWelcomeModal = dynamic(() =>
  import('@components/menu-welcome-modal').then(module => module.MenuWelcomeModal)
);
const MenuPromotionModal = dynamic(() =>
  import('@components/menu-promotion-modal').then(module => module.MenuPromotionModal)
);

const useBrazePromotionContent = ({ promotionProduct, heading, subheading, body, cta, ctaDismiss }) => {
  const { BrazeStore } = useRootStore();
  const { specialOfferCard } = BrazeStore;
  if (!specialOfferCard || !specialOfferCard.promotedProduct) {
    return {
      promotionProduct,
      heading,
      subheading,
      body,
      cta,
      ctaDismiss,
    };
  }
  const product = specialOfferCard.promotedProduct;
  const brazePromotionProduct = {
    name: product.name,
    description: `${product.name} from Braze`,
    plu: specialOfferCard.plu,
    enabled: true,
  };

  return {
    promotionProduct: brazePromotionProduct,
    heading: specialOfferCard?.heading,
    subheading: specialOfferCard?.subheading,
    body: specialOfferCard?.body,
    cta: specialOfferCard?.cta,
    ctaDismiss: specialOfferCard?.ctaDismiss,
    brazeLogClick: specialOfferCard.brazeLogClick,
    brazeLogImpression: specialOfferCard.brazeLogImpression,
  };
};

const Menu = ({ metaContent, floodlightPageLoadEvent }) => {
  // hooks
  const searchFlags = useFeature('search');
  const showDietaryPreferences = useFeature('dietary-preferences');
  const cateringEnabled = useFeature('catering-enabled');
  const router = useRouter();
  const market = useMarket();
  const { colors } = useTheme();

  const {
    BrazeStore,
    CartStore,
    DietaryPreferencesStore,
    MemberStore,
    MenuStore,
    OrderStore,
    OrderingContextStore,
    ProductDetailsState,
    RestaurantStore,
    GroupOrderStore,
    DeliveryStore,
  } = useRootStore();

  const shouldEnableRecommendationEngine = useShouldEnableRecommendationEngine();

  const isClient = useIsClient();
  const { hasReordered } = CartStore;

  // mobx state
  const { itemCount, orderType: cartStoreOrderType } = CartStore;
  const { setSavedDietaryPreferences } = DietaryPreferencesStore;
  const { error, menu, loadMenu, fetchAllergenPDF } = MenuStore;
  const { brazeMergedCategories } = BrazeStore;
  const menuPromotionProduct = get(menu, 'menuPromotion');

  const {
    isUpsellModalEnabled,
    isUpsellModalOpen,
    hasUpsellModalShown,
    closeUpsellModal,
    openUpsellModal,
  } = useUpsellModalHandler({ modalOpeningDelay: 500 });

  const {
    content: upsellContent,
    recommendedProducts: upsellRecommendedProducts,
    newProductIds: upsellNewProductIds,
    brazeLogImpression: upsellBrazeLogImpression,
    brazeLogClick: upsellBrazeLogClick,
  } = useBrazeUpsellContent();

  const specialPromotionContent = {
    promotionProduct: menuPromotionProduct,
    heading: `Can't decide?`,
    subheading: null,
    body: null,
    cta: 'add item',
    ctaDismiss: 'No, thanks',
  };

  const {
    promotionProduct,
    heading: promotionHeading,
    subheading: promotionSubheading,
    body: promotionBody,
    cta: promotionCta,
    ctaDismiss: promotionCtaDismiss,
    brazeLogClick: promotionBrazeLogClick,
    brazeLogImpression: promotionBrazeLogImpression,
  } = useBrazePromotionContent(specialPromotionContent);

  const chosenDietaryPreferences = get(DietaryPreferencesStore, 'dietaryPreferencesList');
  const isLoading = !isClient || MenuStore.loading || !RestaurantStore.loaded || MemberStore.loading;

  // search
  const showSearch = Object.keys(searchFlags)?.includes(CartStore.orderType)
    ? searchFlags[CartStore.orderType]
    : searchFlags.DEFAULT;

  const [showSearchBar, setShowSearchBar] = useState(false);
  const hiddenMenuCategory = useFeature('hidden-menu-category');

  const filteredBrazeMergedCategories = filter(
    brazeMergedCategories,
    category => !hiddenMenuCategory?.categoryHandles?.includes(category.handle)
  );

  const brazeMergedNavSections = filteredBrazeMergedCategories.map(category => ({ id: category.name }));

  const { categoriesWithSearch, searchValue, setSearchValue, hasSearchValue } = useSearch({
    categories: filteredBrazeMergedCategories,
  });
  const hasSearchResults = !!categoriesWithSearch[0]?.products?.length;
  // local state
  const [shouldShowOrderSettingsModal, setShouldShowOrderSettingsModal] = useState(false);
  const [surpriseBannerDismissed, setSurpriseBannerDismissed] = useState(false);
  // Pre-cart S&D teaser from the menu response: the campaign's constant
  // messaging.cta tagline. MenuStore returns null until the user has
  // EXPLICITLY selected a restaurant + order type — the banner must never
  // advertise a campaign against the default restaurant menu.
  const surpriseBanner = MenuStore.surpriseAndDelightBanner;
  // Editing a catering order keeps its saved S&D reward, so the teaser is hidden.
  const showSurpriseBanner = !!surpriseBanner && !surpriseBannerDismissed && !CartStore.isEditingOrder;
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [recommendationEngineModalOpen, setRecommendationEngineModalOpen] = useState(false);
  const [isShowingTabOrderShareModal, setIsShowingTabOrderShareModal] = useState(false);
  const [showCartClearModal, setShowCartClearModal] = useState(false);
  const [visibleCart, setVisibleCart] = useCartVisibility(!isNil(router.query.showCart));
  const [hasDismissedWelcomeModal, setHasDismissedWelcomeModal] = useCookie(MENU_WELCOME_DISMISSED_KEY);
  const showStockShortageAlert = get(menu, 'showStockShortageAlert');
  const showHolidaySurchargeAlert = CartStore.restaurant?.isCateringPickupDateHoliday ?? false;

  const halalAlertData = useFeature('halal-alert');
  const restaurantIdsShowingHalalAlert = map(halalAlertData?.restaurantIds, toString);
  const showHalalAlert = restaurantIdsShowingHalalAlert.includes(CartStore.restaurantId);

  const partialPointRedemptionEnabled = useFeature('partial-point-redemption-enabled');

  const { isRestaurantPreSet, applyPreSet } = useCateringPreSet();

  const showWelcomeModal = isRestaurantPreSet && !hasDismissedWelcomeModal;
  const showOrderSettingsModal = !showWelcomeModal && shouldShowOrderSettingsModal;
  const showPromotionModal =
    !CartStore.isEditingOrder &&
    (!showWelcomeModal || !!hasDismissedWelcomeModal) &&
    !showOrderSettingsModal &&
    isEmpty(chosenDietaryPreferences);

  // Check if user enters catering page with different order type
  useEffect(() => {
    if (
      (router.isReady && itemCount > 0 && MenuStore.menuType && MenuStore.menuType !== 'catering') ||
      (get(OrderingContextStore?.restaurant, 'name', null) &&
        ![ORDER_TYPES.CATERING, ORDER_TYPES.CATERING_DELIVERY].includes(OrderingContextStore.orderType))
    ) {
      setShowCartClearModal(true);
    } else {
      MenuStore.setMenuType('catering');
    }
  }, [router.isReady, itemCount, MenuStore, OrderingContextStore]);

  // Redirect to regular menu if catering feature flag is disabled
  useEffect(() => {
    if (router.isReady && cateringEnabled === false) {
      router.replace('/menu');
    }
  }, [router, cateringEnabled]);

  const { loadPreviousCart } = useCartPersistence();
  useEffect(() => {
    const fetchSavedDietaryPreferences = async () => {
      const savedDietaryPreferences = await localStorage.getItem(SAVED_DIETARY_PREFERENCES_KEY);

      if (isObject(savedDietaryPreferences)) {
        setSavedDietaryPreferences(savedDietaryPreferences);
      }
    };

    (async () => {
      await applyPreSet();

      if (!OrderingContextStore.isPreset && router.isReady) {
        await loadPreviousCart();
      }
      fetchSavedDietaryPreferences();

      if (CartStore.isEditingOrder) {
        setVisibleCart(true);
      } else {
        // Only ever OPEN the drawer here — an order type committed live from inside an
        // already-open drawer (see order-settings-modal-catering's updateSetting) can make
        // isValid transiently true before the async delivery estimate settles, which must
        // not force-close a drawer the member is actively using. Closing is the drawer's own
        // job (dismiss / Save and Continue).
        setShouldShowOrderSettingsModal(prevShouldShow => prevShouldShow || !OrderingContextStore.isValid);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartStoreOrderType, setSavedDietaryPreferences, router.isReady, loadPreviousCart, applyPreSet]);

  // A catering reorder (see past-order handleReorder) always opens the drawer so the member
  // re-confirms order type, date and time. The flag is stripped straight away so it only fires once
  // and doesn't reopen the drawer on later re-renders or a page refresh.
  useEffect(() => {
    if (!router.isReady || isNil(router.query.cateringReorder)) {
      return;
    }
    setShouldShowOrderSettingsModal(true);
    const { cateringReorder, ...query } = router.query;
    router.replace({ query }, undefined, { shallow: true });
  }, [router]);

  useEffect(() => {
    loadMenu();
    fetchAllergenPDF();
  }, [fetchAllergenPDF, loadMenu]);

  useEffect(() => {
    const cartClosedButStillOpenInQuery = !visibleCart && !isNil(router.query.showCart);
    if (cartClosedButStillOpenInQuery) {
      router.push({ query: '' }, undefined, { shallow: true });
    }
  }, [router, visibleCart]);

  useEffect(() => {
    if (hasReordered) {
      analytics.track(ANALYTICS_EVENTS.USER_VIEWED_MENU_AFTER_REORDER);
    }
  }, [hasReordered]);

  useEffect(() => {
    if (router.query.recommendationEngine) {
      setRecommendationEngineModalOpen(true);
    }
  }, [router.query.recommendationEngine]);

  const { getMinDate } = useCateringDateValidation();

  const { shortLeadModal, dismissShortLeadModal } = useCateringShortLeadModal({
    enabled: !showOrderSettingsModal,
    onDismiss: () => {
      const { restaurant } = OrderingContextStore;
      if (restaurant?.cateringWindow) {
        const minDate = getMinDate(restaurant, CartStore.subtotalPrices?.cents || 0);
        OrderingContextStore.updateSettings({
          cateringPickupDate: moment(minDate).format('YYYY-MM-DD'),
          cateringPickupTime: undefined,
        });
      }
      setShouldShowOrderSettingsModal(true);
    },
  });

  // functions
  const viewProduct = useCallback(
    (product, analyticsCategory) => {
      ProductDetailsState.viewProduct({ product, analyticsCategory });
      setProductModalOpen(true);
    },
    [ProductDetailsState, setProductModalOpen]
  );

  const handleCartOnClick = () => {
    setVisibleCart(true);
    if (isUpsellModalEnabled && !hasUpsellModalShown) {
      openUpsellModal();
    }
  };

  const reloadMenu = () => MenuStore.loadMenu();

  const isViewportLGPlus = useMediaQuery({ query: `(min-width: ${LG_PLUS_BREAKPOINT})` });
  const isViewportXl = useMediaQuery({ query: `(min-width: ${XL_PLUS_BREAKPOINT})` });

  const navItemsCount = useMemo(() => {
    let baseNavItems = NAV_ITEMS_COUNT.LG;
    baseNavItems = isViewportLGPlus ? NAV_ITEMS_COUNT.LGPlus : baseNavItems;
    baseNavItems = isViewportXl ? NAV_ITEMS_COUNT.XLPlus : baseNavItems;

    // If we are showing the view group order button, we need fewer nav items to compensate.
    return GroupOrderStore.isInGroup ? baseNavItems - 1 : baseNavItems;
  }, [GroupOrderStore.isInGroup, isViewportLGPlus, isViewportXl]);

  // if a menu loading error occurs on server side only, we can get in a bad state where the server renders <Error /> but the client doesn't,
  // and the two get spliced together. Hence, we only show this error on client. See https://app.clickup.com/t/39gr2pp
  if (isClient && error) {
    return <Error showRefreshButton reload={reloadMenu} />;
  }

  return (
    <Layout
      floodlightPageLoadEvent={floodlightPageLoadEvent}
      hasBottomPattern
      hasTopBanner={showSurpriseBanner}
      metaContent={metaContent}
      onScreenButtonProps={{
        itemCount,
        onClick: handleCartOnClick,
        price: OrderStore.subTotal.formattedPrice,
      }}
      title={`Menu | Nando’s ${market === MARKET.AUSTRALIA ? 'Australia' : 'New Zealand'}`}
    >
      <Head>
        <meta
          name="description"
          content="Browse our full menu selection of PERi-PERi Grilled Chicken, Burgers, Wraps, Pitas, Vegetarian & Kids options, desserts, drinks, and mouth-watering sides."
        />
      </Head>
      {showSurpriseBanner && (
        <SurpriseAndDelightGlobalBanner
          message={surpriseBanner.cta}
          onDismiss={() => setSurpriseBannerDismissed(true)}
        />
      )}
      <ErrorBoundary>
        <OrderSettingsBanner
          isSlantedHeader
          isRestaurantPreSet={isRestaurantPreSet}
          bannerHeading={['Catering', 'Menu']}
          onClick={() => {
            setShouldShowOrderSettingsModal(true);
          }}
          catering
        />
      </ErrorBoundary>
      {isLoading ? (
        <SkeletonLoader baseColor={colors.warmWhite100} highlightColor={colors.warmWhite300} height="2rem" />
      ) : (
        <>
          <InPageNav
            cartOnClick={handleCartOnClick}
            groupInfoOnClick={() => setIsShowingTabOrderShareModal(true)}
            navItemsCount={navItemsCount}
            quantity={itemCount}
            sections={brazeMergedNavSections}
            showDietaryPreferences={showDietaryPreferences}
            showGroupInfoButton={GroupOrderStore.isInGroup}
            onBannerClick={() => {
              setShouldShowOrderSettingsModal(true);
            }}
            setShowSearchBar={() => setShowSearchBar(true)}
          />
          {showSearch && (
            <SearchBar
              id="menu-search-bar"
              onClose={() => setShowSearchBar(false)}
              showSearch={showSearchBar}
              onSearchChange={setSearchValue}
              searchValue={searchValue}
              width={{ _: 12 / 12, lg: 6 / 12 }}
            />
          )}
        </>
      )}
      {showStockShortageAlert && <StockShortageAlert page="menu" />}

      {showHolidaySurchargeAlert && <HolidaySurchargeAlert />}

      {!isLoading && shouldEnableRecommendationEngine && !GroupOrderStore.isInGroup && (
        <MenuActionCard
          iconName="recEngine"
          text="Help me choose"
          onClick={() => setRecommendationEngineModalOpen(true)}
          display={{ xs: 'none', lg: 'flex' }}
          variant="fixed"
        />
      )}

      {showHalalAlert && <HalalAlert />}
      <ErrorBoundary>
        {!isLoading && (
          <Flex
            flexDirection="column"
            gap={{ xs: '40px', md: '80px' }}
            mt={{ xs: '24px', md: '60px' }}
            mb={{ xs: '64px', md: '120px' }}
          >
            {!hasSearchValue && shouldEnableRecommendationEngine && !GroupOrderStore.isInGroup && (
              <Container display={{ xs: 'block', lg: 'none' }}>
                <Flex>
                  <MenuActionCard
                    iconName="recEngine"
                    text="Help me choose"
                    onClick={() => setRecommendationEngineModalOpen(true)}
                  />
                </Flex>
              </Container>
            )}

            {!CartStore.isEditingOrder && MemberStore.isSignedIn && !GroupOrderStore.isGroupInvitee && !hasSearchValue && (
              <>
                <MenuPastOrders
                  type="catering"
                  onReorder={() => setVisibleCart(true)}
                  bannerSlot={partialPointRedemptionEnabled ? <PointsRedemptionBanner /> : null}
                />
                <PromotionList analyticsLocation="Menu" />
              </>
            )}

            {!CartStore.isEditingOrder &&
              !MemberStore.isSignedIn &&
              partialPointRedemptionEnabled &&
              !GroupOrderStore.isGroupInvitee &&
              !hasSearchValue && (
                <Container>
                  <Flex
                    justifyContent="space-between"
                    alignItems={{ md: 'center' }}
                    flexDirection={{ xs: 'column', md: 'row' }}
                    mb={{ xs: '-12px', md: '-40px' }}
                    gap="16px"
                  >
                    <H4 mb="0">Welcome!</H4>
                    <PointsRedemptionBanner />
                  </Flex>
                </Container>
              )}

            {hasSearchValue && !hasSearchResults && (
              <Container>
                <Row>
                  <Col textAlign="center" width={{ _: 10 / 12, md: 6 / 12 }} offset={[1 / 12, 1 / 12, 3 / 12]}>
                    <DefaultState
                      subtitle={`No results for "${searchValue}". Maybe check your spelling, search for something else, or just browse category tabs above`}
                      title="Oops"
                    />
                  </Col>
                </Row>
              </Container>
            )}
            {(!hasSearchValue || hasSearchResults) && (
              <ProductCategoryList
                quantityPicker
                hideDescription
                categories={categoriesWithSearch}
                CardComponent={MenuItemCard}
                viewProduct={viewProduct}
                isDelivery={CartStore.isDelivery}
                isDeliveryDiscountActive={DeliveryStore.isDeliveryDiscountActive}
                isSearch
              />
            )}
            <MenuDisclaimer />
          </Flex>
        )}
      </ErrorBoundary>
      <CartPreviewModal
        visibleCart={visibleCart}
        setVisibleCart={setVisibleCart}
        setShouldShowOrderSettingsModal={setShouldShowOrderSettingsModal}
      />
      {shouldEnableRecommendationEngine && (
        <ErrorBoundary>
          <RecommendationEngineModal
            modalOpen={recommendationEngineModalOpen}
            handleModalClose={() => setRecommendationEngineModalOpen(false)}
            setVisibleCart={setVisibleCart}
          />
        </ErrorBoundary>
      )}
      {productModalOpen && (
        <ProductDetailModal modalOpen={productModalOpen} handleModalClose={() => setProductModalOpen(false)} />
      )}
      {showWelcomeModal ? (
        <MenuWelcomeModal
          onClose={() => {
            setHasDismissedWelcomeModal(true, { expires: 1 });
          }}
        />
      ) : null}
      {!hasUpsellModalShown && isUpsellModalOpen && (
        <UpsellModal
          modalOpen={isUpsellModalOpen}
          handleModalClose={closeUpsellModal}
          recommendedProducts={upsellRecommendedProducts}
          newProductIds={upsellNewProductIds}
          content={upsellContent}
          brazeLogImpression={upsellBrazeLogImpression}
          brazeLogClick={upsellBrazeLogClick}
        />
      )}
      {showOrderSettingsModal && !showCartClearModal && (
        <OrderSettingsModalCatering onClose={() => setShouldShowOrderSettingsModal(false)} />
      )}
      {shortLeadModal && (
        <ShortLeadTimeErrorModal
          open
          variant={shortLeadModal.variant}
          maxValue={shortLeadModal.maxValue}
          standardLeadHours={shortLeadModal.standardLeadHours}
          cutoffTime={shortLeadModal.cutoffTime}
          requestedTime={shortLeadModal.requestedTime}
          onClose={dismissShortLeadModal}
        />
      )}
      {isLoading ? null : (
        <>
          {showPromotionModal && !isEmpty(promotionProduct) ? (
            <MenuPromotionModal
              brazeLogImpression={promotionBrazeLogImpression}
              brazeLogClick={promotionBrazeLogClick}
              viewProduct={viewProduct}
              menuPromotion={promotionProduct}
              content={{
                body: promotionBody,
                cta: promotionCta,
                ctaDismiss: promotionCtaDismiss,
                heading: promotionHeading,
                subheading: promotionSubheading,
              }}
            />
          ) : null}
        </>
      )}
      <TabOrderShareModal
        handleModalClose={() => setIsShowingTabOrderShareModal(false)}
        modalOpen={isShowingTabOrderShareModal}
      />
      <CartClearModal
        open={showCartClearModal}
        returnButtonText="return to my order"
        continueButtonText="continue to catering"
        onReturnToOriginal={() => {
          router.push('/menu');
        }}
        onContinueToSecondary={() => {
          OrderStore.clearSessionData();
          GroupOrderStore.reset();
          MenuStore.setMenuType('catering');

          setShowCartClearModal(false);
        }}
      />
    </Layout>
  );
};

Menu.propTypes = {
  floodlightPageLoadEvent: floodlightPageLoadEventPropTypes,
  metaContent: PropTypes.shape(seoPropTypes),
};

Menu.defaultProps = {
  floodlightPageLoadEvent: undefined,
  metaContent: seoPropDefaults,
};

export const getStaticProps = withAppProps(async () => {
  const data = await fetchCMSContent({
    uri: 'menu',
    slug: 'menu',
  });

  const page = data.id ? data : null;

  return {
    props: { ...page, generatedAt: new Date().toISOString() },
    revalidate: 60 * 60, // 1 hour
  };
});

export default observer(Menu);
